import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronDown, LogIn, LayoutDashboard, LogOut } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { trackEvent } from "@/lib/tracking";
import api from "@/lib/api";

const NAV = [
    { id: "leistungen", key: "nav.services" },
    { id: "projekte", key: "nav.cases" },
    { id: "faq", key: "nav.faq" },
    { id: "kontakt", key: "nav.contact" },
];

const CLIENT_PORTAL_URL = "/login";

export default function Header() {
    const { t, locale, setLocale } = useI18n();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [settings, setSettings] = useState({ whatsapp_number: "4915231656312", phone_number: "+4915231656312", whatsapp_prefill: "" });
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 14);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
    }, []);

    const wa = `https://wa.me/${(settings.whatsapp_number || "4915231656312").replace(/\D/g, "")}?text=${encodeURIComponent(settings.whatsapp_prefill || "Hallo BA Media Performance, ich interessiere mich für ein Lead-Funnel-System für mein Unternehmen.")}`;
    const tel = `tel:${(settings.phone_number || "+4915231656312").replace(/\s/g, "")}`;

    const isAdmin = loc.pathname.startsWith("/admin");
    if (isAdmin) return null;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-[#0F172A]/80 border-b border-white/10" : "bg-transparent"}`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center gap-6">
                <Link to="/" className="flex items-center group" data-testid="header-logo-link" aria-label="BA Media Performance home">
                    <picture>
                        <source srcSet="/bamedia-logo.webp" type="image/webp" />
                        <img
                            src="/bamedia-logo.png"
                            alt="BA Media Performance"
                            width="280"
                            height="280"
                            fetchPriority="high"
                            decoding="async"
                            className="h-20 w-20 sm:h-24 sm:w-24 lg:h-32 lg:w-32 object-contain group-hover:scale-105 transition-transform"
                        />
                    </picture>
                    <span className="sr-only">BA Media Performance</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1 ml-4 text-sm">
                    {NAV.map((n) => (
                        <a
                            key={n.id}
                            href={`/#${n.id}`}
                            data-testid={`nav-${n.id}`}
                            className="px-3 py-2 rounded-lg text-white/80 hover:text-white hover:bg-white/5 transition-colors font-medium"
                        >
                            {t(n.key)}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 ml-auto">
                    <LangToggle locale={locale} setLocale={setLocale} />
                    {user && user.role === "client" ? (
                        <>
                            <a
                                href="/dashboard"
                                data-testid="header-client-dashboard-btn"
                                className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-xl border border-white/15 bg-white/[0.04] text-white/90 hover:bg-white/10 transition-colors"
                            >
                                <LayoutDashboard className="size-4 text-[#10B981]" /> Dashboard
                            </a>
                            <button
                                type="button"
                                onClick={async () => { await logout(); navigate("/"); }}
                                data-testid="header-client-logout-btn"
                                className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-xl text-white/85 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <LogOut className="size-4" /> Logout
                            </button>
                        </>
                    ) : user && user.role === "admin" ? (
                        <a
                            href="/admin/dashboard"
                            data-testid="header-admin-dashboard-btn"
                            className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-xl border border-white/15 bg-white/[0.04] text-white/90 hover:bg-white/10 transition-colors"
                        >
                            <LayoutDashboard className="size-4 text-[#C8A96B]" /> Admin
                        </a>
                    ) : (
                        <a
                            href={CLIENT_PORTAL_URL}
                            onClick={() => trackEvent("cta_click", { position: "header_client_portal" })}
                            data-testid="header-client-login-btn"
                            className="hidden md:inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-xl border border-white/15 bg-white/[0.04] text-white/90 hover:bg-white/10 hover:text-white hover:border-white/25 transition-colors"
                            title={t("nav.client_login_hint")}
                        >
                            <LogIn className="size-4 text-[#C8A96B]" /> {t("nav.client_login")}
                        </a>
                    )}
                    <a
                        href={wa}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent("whatsapp_click", { position: "header" })}
                        data-testid="header-whatsapp-btn"
                        className="hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <MessageCircle className="size-4" /> {t("cta.whatsapp")}
                    </a>
                    <a
                        href={tel}
                        onClick={() => trackEvent("call_click", { position: "header" })}
                        data-testid="header-call-btn"
                        className="hidden lg:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl text-white/90 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <Phone className="size-4" /> {t("cta.call")}
                    </a>
                    <a
                        href="/#kontakt"
                        onClick={() => trackEvent("cta_click", { position: "header_primary" })}
                        data-testid="header-cta-analyse"
                        className="hidden md:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-[#047857] text-white shadow-[0_0_24px_rgba(16,185,129,0.25)] hover:bg-[#065F46] transition"
                    >
                        {t("cta.analyse")}
                    </a>
                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl text-white hover:bg-white/10"
                        onClick={() => setOpen((s) => !s)}
                        data-testid="header-mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X className="size-5" /> : <Menu className="size-5" />}
                    </button>
                </div>
            </div>

            {open ? (
                <div className="lg:hidden border-t border-white/10 bg-[#0F172A]/95 backdrop-blur-xl" data-testid="header-mobile-menu">
                    <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <a
                                key={n.id}
                                href={`/#${n.id}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.id}`}
                                className="px-3 py-3 rounded-lg text-white/85 hover:bg-white/5"
                            >
                                {t(n.key)}
                            </a>
                        ))}
                        <a
                            href="/#kontakt"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-cta"
                            className="mt-2 px-3 py-3 rounded-xl bg-[#10B981] text-white text-center font-semibold"
                        >
                            {t("cta.analyse")}
                        </a>
                        <a
                            href={user?.role === "client" ? "/dashboard" : user?.role === "admin" ? "/admin/dashboard" : CLIENT_PORTAL_URL}
                            onClick={() => { setOpen(false); trackEvent("cta_click", { position: "mobile_client_portal" }); }}
                            data-testid="mobile-nav-client-login"
                            className="mt-1 px-3 py-3 rounded-xl border border-white/15 bg-white/[0.04] text-white text-center font-semibold inline-flex items-center justify-center gap-2"
                        >
                            {user ? <LayoutDashboard className="size-4 text-[#10B981]" /> : <LogIn className="size-4 text-[#C8A96B]" />}
                            {user?.role === "client" ? "Dashboard" : user?.role === "admin" ? "Admin Dashboard" : t("nav.client_login")}
                        </a>
                        {user ? (
                            <button
                                type="button"
                                onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                                data-testid="mobile-nav-logout"
                                className="mt-1 px-3 py-3 rounded-xl text-white/85 hover:bg-white/10 font-semibold inline-flex items-center justify-center gap-2"
                            >
                                <LogOut className="size-4" /> Logout
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </header>
    );
}

function LangToggle({ locale, setLocale }) {
    return (
        <div className="hidden sm:flex items-center rounded-xl bg-white/5 border border-white/10 p-0.5 text-xs">
            {["de", "en"].map((l) => (
                <button
                    type="button"
                    key={l}
                    onClick={() => setLocale(l)}
                    data-testid={`lang-toggle-${l}`}
                    className={`px-2.5 py-1.5 rounded-lg uppercase tracking-wider font-semibold transition-colors ${
                        locale === l ? "bg-white text-[#0F172A]" : "text-white/70 hover:text-white"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
}
